import { Navigate, useLocation } from 'react-router-dom';
import { useOptionalAuth } from './AuthProvider';
import React from 'react';
import { motion } from 'framer-motion';
import { useCurrentSubscription } from '@/hooks/useCurrentSubscription';
import { useEntitlementsSettled } from '@/hooks/useEntitlementsSettled';

const rabbit_icon = '/assets/home/skating-rabbit.svg';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const auth = useOptionalAuth();
  const user = auth?.user ?? null;
  const loading = auth?.loading ?? true;
  const profileSynced = auth?.profileSynced ?? false;
  const location = useLocation();

  const isSuperAdmin = !!(user as any)?.is_super_admin;
  const isAgency = !!(user as any)?.is_agency;
  const isRegularUser = !!user && !isSuperAdmin && !isAgency;
  const organizationId = (user as any)?.organization_id;

  // Hold the loader open during the FIRST subscription fetch so the post-login
  // navigation feels identical to a hard refresh. Without this, login() flips
  // auth.loading=false synchronously before navigating, so the rabbit never
  // shows and the sidebar entitlements skeleton flashes for <100ms before the
  // real menu renders — team members report it as "no loader on login, but
  // shows on refresh". Refresh path keeps working unchanged: AuthProvider's
  // initial getProfile holds `loading` true long enough for the rabbit to
  // appear, then this gate keeps it on screen until entitlements resolve.
  //
  // Scope: only regular team members. Super-admins are redirected to /admin/*
  // a few lines down (no subscription needed), and agency users have a
  // different subscription pathway via AgencyRoute. The `enabled` flag keeps
  // the query dormant for them so we never fire an extra network call.
  const { data: subscriptionData, isLoading: subscriptionLoading, isError: subscriptionError } =
    useCurrentSubscription({
      enabled: !loading && isRegularUser && !!organizationId,
    });

  // `placeholderData: keepPreviousData` on the hook means `data` is undefined
  // ONLY on the very first fetch in this browser session. Once anything has
  // resolved (success, null = no-sub, or error), we release the gate so the
  // loader doesn't reappear on background refetches or staleTime expiries.
  const isBootstrappingEntitlements =
    isRegularUser && subscriptionLoading && subscriptionData === undefined && !subscriptionError;

  // Wait for the entitlements payload to actually contain plan features (or
  // a confirmed no-sub state). Without this, the rabbit loader released as
  // soon as `subscriptionData !== undefined` even when the inner
  // `entitlements.features` map was still empty — AppSidebar would then
  // render with only ALWAYS_ENABLED_FEATURES and "fill in" plan items a
  // moment later. That visible stagger is what team members report.
  const entitlementsSettled = useEntitlementsSettled(isRegularUser && !!organizationId);

  // Hold the loader until the initial /auth/getProfile call has resolved.
  // On warm-start with a stored user, `loading` flips false synchronously
  // but `menu_permissions` (and other fields) can still change once the
  // backend response lands — causing the sidebar to re-filter mid-paint.
  // `profileSynced === false` blocks; `undefined` (older mocks / consumers
  // that haven't migrated) is treated as "not blocking" to preserve test
  // compatibility — the live AuthProvider always sets the flag explicitly.
  const profileSyncing = profileSynced === false;
  const isActuallyLoading =
    loading ||
    profileSyncing ||
    isBootstrappingEntitlements ||
    (isRegularUser && !!organizationId && !entitlementsSettled);

  // Minimum-visible window. On dev / warm backends, `/subscriptions/current`
  // often resolves in <50 ms — fast enough that the loader gate flips closed
  // inside a single paint and the user sees nothing. We hold the loader on
  // screen for at least MIN_VISIBLE_MS once it has been shown, so a fast
  // network can no longer make it invisible. Guarded by `everLoadedRef` so
  // routes that never need the loader (e.g. unauthenticated → /auth redirect,
  // super-admin redirect, agency render) don't pay any extra delay.
  const MIN_VISIBLE_MS = 450;
  const everLoadedRef = React.useRef(isActuallyLoading);
  const [holdOpen, setHoldOpen] = React.useState(false);
  React.useEffect(() => {
    if (isActuallyLoading) {
      everLoadedRef.current = true;
      setHoldOpen(true);
      return;
    }
    if (!everLoadedRef.current) {
      setHoldOpen(false);
      return;
    }
    const t = setTimeout(() => setHoldOpen(false), MIN_VISIBLE_MS);
    return () => clearTimeout(t);
  }, [isActuallyLoading]);

  if (isActuallyLoading || holdOpen) {
    return (
      <div
        className="min-h-screen flex bg-[#F5F7F9]"
        aria-busy="true"
        aria-label="Loading"
      >
        {/* Sidebar skeleton — visually identical to AppSidebar's entitlements-loading
            skeleton so when AppShell mounts there's no perceptible swap. Same row
            shape (icon + label), same row count, same shimmer utility class. */}
        <aside className="hidden md:flex flex-col w-[240px] border-r border-[#E2E8F0] bg-white px-3 py-2 gap-2">
          <div className="wenext-shimmer h-8 w-32 rounded-md mt-2 mb-4" />
          <div className="flex flex-col gap-2 pt-[35px]">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="flex items-center gap-2.5 py-1.5 px-[10px]">
                <div className="wenext-shimmer w-5 h-5 rounded-md flex-shrink-0" />
                <div className="wenext-shimmer h-4 rounded-md" style={{ width: `${55 + ((i * 13) % 35)}%` }} />
              </div>
            ))}
          </div>
        </aside>
        {/* Main column */}
        <main className="flex-1 flex flex-col min-w-0">
          {/* Topbar skeleton */}
          <div className="h-14 border-b border-[#E2E8F0] bg-white px-4 flex items-center gap-3">
            <div className="wenext-shimmer h-9 flex-1 max-w-[420px] rounded-md" />
            <div className="ml-auto flex items-center gap-2">
              <div className="wenext-shimmer h-8 w-28 rounded-md" />
              <div className="wenext-shimmer h-8 w-24 rounded-md" />
              <div className="wenext-shimmer h-8 w-8 rounded-full" />
            </div>
          </div>
          {/* Rabbit mascot in main area — same visual as Dashboard's loader so there's no flash on handoff */}
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <motion.div
                animate={{ x: [-15, 15, -15], rotate: [-5, 5, -5] }}
                transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                className="mb-6"
              >
                <img src={rabbit_icon} alt="Loading..." className="h-28 w-28 mx-auto" />
              </motion.div>
              <h3 className="text-xl font-bold mb-2 tracking-tight text-[#08090A]">Loading Dashboard</h3>
              <p className="text-muted-foreground animate-pulse">Please wait while we load your business accounts...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!user) return <Navigate to="/auth" replace />;

  const isAppRoute = location.pathname.startsWith('/home/');

  if (isSuperAdmin && isAppRoute) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  return <>{children}</>;
}
